import type { UnitName } from "@sinch/core/entities/serverEnums.types";
import { isNil } from "ramda";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useUnitName } from "@sinch/components/application/Rate/useUnitType";
import { NotedValue } from "@sinch/views/RateBuilder/RateCalculator/NotedValue";

/**
 * Formatted units, prefixed by the units before rounding when a rounding changed them: "5.82h ≈ 6h"
 * - "≈" is the symbol the rounding chips in the builder use
 * - both values share the unit format of the column, so Unit x Amount = Total stays easy to check
 */
export const UnitCell: FC<{
  unit: number | null | undefined;
  unroundedUnit: number | null | undefined;
  unitName: UnitName | string | null | undefined;
}> = ({ unit, unroundedUnit, unitName }) => {
  const { t } = useTranslation();
  const { getUnitFormatter } = useUnitName();
  if (isNil(unit)) {
    return null;
  }
  const format = (value: number): string => (unitName ? getUnitFormatter(unitName as UnitName)?.(value) : undefined) ?? String(value);
  const formattedUnit = format(unit);
  const original = isNil(unroundedUnit) ? null : format(unroundedUnit);
  // a rounding by less than the displayed precision would read as "6h ≈ 6h"
  const showOriginal = original !== null && original !== formattedUnit;
  return (
    <NotedValue
      note={showOriginal ? `${original} ≈` : null}
      noteTitle={showOriginal ? t("Ratelist.roundedFrom", { value: original }) : undefined}
    >
      {formattedUnit}
    </NotedValue>
  );
};
